import { createSelector } from '@reduxjs/toolkit';
import { UID } from 'agora-rtc-react';
import { selectCommunitySettings } from 'community/api/selectors';
import { MediaTrackState } from 'domain/Broadcast';
import { AgoraProxyMode } from 'domain/event';
import { inRange } from 'lodash-es';
import { RootState } from 'store';
import {
  selectHasCameraAccess,
  selectHasMicrophoneAccess,
  selectIsAudioEnabled,
  selectIsVideoEnabled,
} from 'user/state/selectors';

import { selectCurrentEventPhase } from '../event/selectors';
import { EventPhase } from '../event/types';
import {
  NETWORK_QUALITY_BOTTOM_THRESHOLD,
  NETWORK_QUALITY_TOP_THRESHOLD,
  proxyModeSettingToAgoraProxyMode,
} from './constants';
import { NetworkErrorKey } from './types';

const selectAllAgoraState = (state: RootState) => state.agora;

export const selectIsJoinedToAgora = createSelector(selectAllAgoraState, (state) => state.isJoined);

const selectNetworkQuality = createSelector(selectAllAgoraState, (state) => state.networkQuality);

const selectAudioTrack = createSelector(selectAllAgoraState, (state) => state.audioTrack);
const selectVideoTrack = createSelector(selectAllAgoraState, (state) => state.videoTrack);

export const selectAudioTrackState = createSelector(selectAudioTrack, (audioTrack) => audioTrack.state);
export const selectVideoTrackState = createSelector(selectVideoTrack, (videoTrack) => videoTrack.state);

export const selectAudioTrackError = createSelector(selectAudioTrack, (audioTrack) => audioTrack.error);
export const selectVideoTrackError = createSelector(selectVideoTrack, (videoTrack) => videoTrack.error);

export const selectIsAudioTrackReadyToBeCreated = createSelector(
  selectAudioTrackError,
  selectIsAudioEnabled,
  selectHasMicrophoneAccess,
  (error, isAudioEnabled, hasMicrophoneAccess) => error === null && isAudioEnabled && !!hasMicrophoneAccess
);
export const selectIsVideoTrackReadyToBeCreated = createSelector(
  selectVideoTrackError,
  selectIsVideoEnabled,
  selectHasCameraAccess,
  (error, isVideoEnabled, hasCameraAccess) => error === null && isVideoEnabled && !!hasCameraAccess
);

export const selectCurrentCameraDeviceId = createSelector(selectVideoTrack, (videoTrack) => videoTrack.currentDeviceId);

export const selectCurrentMicrophoneDeviceId = createSelector(
  selectAudioTrack,
  (audioTrack) => audioTrack.currentDeviceId
);

const selectAgoraRole = createSelector(selectAllAgoraState, (state) => state.role);
export const selectIsAgoraHost = createSelector(selectAgoraRole, (role) => role === 'host');

export const selectIsAudioTrackReadyToPublish = createSelector(
  selectIsJoinedToAgora,
  selectAudioTrack,
  (isJoined, audioTrack) => isJoined && audioTrack.state === MediaTrackState.Enabled
);
export const selectIsVideoTrackReadyToPublish = createSelector(
  selectIsJoinedToAgora,
  selectVideoTrack,
  (isJoined, videoTrack) => isJoined && videoTrack.state === MediaTrackState.Enabled
);

export const selectShouldPublishAudioTrackForBroadcast = createSelector(
  selectIsAudioTrackReadyToPublish,
  selectIsAgoraHost,
  (isReady, isAgoraHost) => isReady && isAgoraHost
);
export const selectShouldPublishVideoTrackForBroadcast = createSelector(
  selectIsVideoTrackReadyToPublish,
  selectIsAgoraHost,
  (isReady, isAgoraHost) => isReady && isAgoraHost
);

export const selectIsDownlinkConnectionUnstable = createSelector(selectNetworkQuality, (networkQuality) => {
  const sum = networkQuality.reduce((acc, current) => acc + current.downlinkNetworkQuality, 0);

  return inRange(sum, NETWORK_QUALITY_BOTTOM_THRESHOLD, NETWORK_QUALITY_TOP_THRESHOLD + 1);
});

export const selectIsUplinkConnectionUnstable = createSelector(selectNetworkQuality, (networkQuality) => {
  const sum = networkQuality.reduce((acc, current) => acc + current.uplinkNetworkQuality, 0);

  return inRange(sum, NETWORK_QUALITY_BOTTOM_THRESHOLD, NETWORK_QUALITY_TOP_THRESHOLD + 1);
});

export const selectConnectionQualityMessageKey = createSelector(
  selectIsJoinedToAgora,
  selectCurrentEventPhase,
  selectAgoraRole,
  selectIsDownlinkConnectionUnstable,
  selectIsUplinkConnectionUnstable,
  (isJoined, eventPhase, role, isDownlinkConnectionUnstable, isUplinkConnectionUnstable) => {
    if (!isJoined) {
      return null;
    }

    const uplinkConnectionMatters =
      (eventPhase === EventPhase.Broadcast && role === 'host') || eventPhase === EventPhase.Nattering;

    if (uplinkConnectionMatters && isUplinkConnectionUnstable && isDownlinkConnectionUnstable) {
      return NetworkErrorKey.UnstableConnection;
    }

    if (uplinkConnectionMatters && isUplinkConnectionUnstable) {
      return NetworkErrorKey.UnstableUplinkConnection;
    }

    if (isDownlinkConnectionUnstable) {
      return NetworkErrorKey.UnstableDownlinkConnection;
    }

    return null;
  }
);

export const selectIsPoorNetworkQuality = createSelector(
  selectIsDownlinkConnectionUnstable,
  selectIsUplinkConnectionUnstable,
  (isDownlinkConnectionUnstable, isUplinkConnectionUnstable) =>
    isDownlinkConnectionUnstable || isUplinkConnectionUnstable
);

export const selectAgoraSupportedCloudProxyOptions = createSelector(
  selectAllAgoraState,
  (state) => state.supportedCloudProxyOptions
);

const selectCommunityLevelProxyMode = createSelector(selectCommunitySettings, (communitySettings) =>
  communitySettings?.proxyMode ? proxyModeSettingToAgoraProxyMode[communitySettings.proxyMode] : undefined
);

/**
 * priority:
 * 1. no cloud proxy
 * 2. UDP
 * 3. TCP
 */
export const selectAgoraProxyMode = createSelector(
  selectAgoraSupportedCloudProxyOptions,
  selectCommunityLevelProxyMode,
  (supportedCloudProxyOptions, communityLevelProxyMode) => {
    if (communityLevelProxyMode !== undefined) {
      return communityLevelProxyMode;
    }

    if (!supportedCloudProxyOptions.length) {
      return null;
    }

    if (supportedCloudProxyOptions.includes(AgoraProxyMode.NoProxy)) return AgoraProxyMode.NoProxy;
    if (supportedCloudProxyOptions.includes(AgoraProxyMode.ForceUdp)) return AgoraProxyMode.ForceUdp;
    if (supportedCloudProxyOptions.includes(AgoraProxyMode.ForceTcp)) return AgoraProxyMode.ForceTcp;

    return null;
  }
);

export const selectAgoraConnectionTestState = createSelector(selectAllAgoraState, (state) => state.connectionTestState);

export const selectIsAgoraConnectionTestReady = createSelector(
  selectAgoraProxyMode,
  selectCommunityLevelProxyMode,
  selectAgoraConnectionTestState,
  (agoraProxyMode, communityLevelProxyMode, state) =>
    agoraProxyMode === AgoraProxyMode.NoProxy ||
    communityLevelProxyMode !== undefined ||
    state === 'failed' ||
    state === 'completed'
);

export const selectIsAgoraConnectionTestRequired = createSelector(
  selectAgoraProxyMode,
  selectAgoraConnectionTestState,
  (agoraProxyMode, state) => agoraProxyMode === null && (state === 'idle' || state === 'failed')
);

const selectRemoteNetworkQuality = createSelector(selectAllAgoraState, (state) => state.remoteNetworkQuality);

export const selectNetworkQualityForRemoteUser = createSelector(
  selectRemoteNetworkQuality,
  (_state: RootState, userId: UID | undefined) => userId,
  (quality, userId) => (userId ? quality[userId]?.uplinkNetworkQuality : undefined)
);
